/**
 * The day detail and edit view. FR-302, FR-201..FR-208, FR-214.
 *
 * ## Saving is implicit
 *
 * Every control commits on change. There is no Save button, so there is nothing
 * to forget — which matters most for the person this app is designed around:
 * in pain, in a hurry, or being watched (UI design Section 4). The cost is that
 * every control needs a working "clear" path, since an accidental tap is now a
 * saved value.
 *
 * ## Ordering
 *
 * Flow first, because it is by far the most-logged field. Everything below it is
 * optional and never blocks anything.
 */
import { t } from '@copy/index'
import { type EnergyLevel, type FlowLevel, type ISODate } from '@domain/index'
import { useEntries, useSettings } from '@contexts/index'
import {
  ChoiceRow,
  FlowPicker,
  NoteField,
  Notice,
  PainScale,
  Sheet,
  SymptomToggles,
  type ChoiceOption,
} from '@shared/ui'

const ENERGY_OPTIONS: readonly ChoiceOption<EnergyLevel>[] = [
  { value: 'low', label: t('entry.energyLevel.low') },
  { value: 'medium', label: t('entry.energyLevel.medium') },
  { value: 'high', label: t('entry.energyLevel.high') },
]

export interface DaySheetProps {
  date: ISODate | undefined
  onClose: () => void
}

export function DaySheet({ date, onClose }: DaySheetProps) {
  const { entries, today, update, markEmpty, remove } = useEntries()
  const { activeSymptoms, isPremium } = useSettings()

  if (date === undefined) return null

  const entry = entries.get(date)
  const readable = t.date(new Date(`${date}T12:00:00`))
  const isFuture = date > today
  const symptoms = entry?.symptoms ?? []
  const trialExpired = !isPremium && entries.size >= 90 && !entries.has(date)

  const toggleSymptom = (id: string) => {
    const next = symptoms.includes(id)
      ? symptoms.filter((s) => s !== id)
      : [...symptoms, id]
    void update(date, { symptoms: next.length > 0 ? next : undefined })
  }

  return (
    <Sheet open onClose={onClose} title={t('entry.title', { date: readable })}>
      <header className="flex items-start justify-between pb-4">
        <div>
          <h2 className="text-base font-medium">{readable}</h2>
          {date === today ? (
            <p className="text-xs text-ink-muted">{t('calendar.today')}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-line text-sm transition-all duration-200 hover:bg-line/40 hover:text-ink active:scale-95"
          aria-label={t('entry.close')}
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </header>

      {trialExpired ? (
        <div className="pb-4">
          <Notice
            tone="warning"
            action={{
              label: t('storage.upgrade'),
              onClick: () => {
                onClose()
                window.history.pushState({}, '', '/data')
                window.dispatchEvent(new PopStateEvent('popstate'))
              },
            }}
          >
            {t('storage.saveFailedPremium')}
          </Notice>
        </div>
      ) : null}

      {isFuture ? (
        <div className="pb-4">
          <Notice tone="info">{t('entry.futureDate')}</Notice>
        </div>
      ) : null}

      <fieldset disabled={trialExpired} className="contents">
        <div className="flex flex-col gap-5">
          <FlowPicker
            date={date}
            value={entry?.flow}
            onChange={(flow: FlowLevel | undefined) => void update(date, { flow })}
          />

          <PainScale
            value={entry?.pain}
            onChange={(pain) => void update(date, { pain })}
          />

          <ChoiceRow
            legend={t('entry.energy')}
            options={ENERGY_OPTIONS}
            value={entry?.energy}
            onChange={(energy) => void update(date, { energy })}
          />

          {activeSymptoms.length > 0 ? (
            <SymptomToggles
              legend={t('entry.symptoms')}
              categoryLabel={(category) => t(`symptomCategory.${category}`)}
              options={activeSymptoms.map((def) => ({
                id: def.id,
                label: def.label ?? t(def.labelKey),
                category: def.category,
              }))}
              selected={symptoms}
              onToggle={toggleSymptom}
            />
          ) : null}

          <NoteField
            key={date}
            label={t('entry.note')}
            placeholder={t('entry.notePlaceholder')}
            value={entry?.note}
            onCommit={(note) => void update(date, { note })}
          />

          <div className="flex flex-col gap-2 border-t border-line pt-4">
            {entry === undefined ? (
              <>
                <button
                  type="button"
                  onClick={() => void markEmpty(date)}
                  className="min-h-11 rounded-lg border border-line text-sm"
                >
                  {t('entry.nothingToRecord')}
                </button>
                <p className="text-xs text-ink-muted">{t('entry.nothingToRecordHint')}</p>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  void remove(date)
                  onClose()
                }}
                className="min-h-11 rounded-lg border border-line text-sm text-ink-muted"
              >
                {t('entry.clear')}
              </button>
            )}
          </div>
        </div>
      </fieldset>
    </Sheet>
  )
}
